import { useEffect, useState, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { PaperPlaneTilt, ChatCircleText } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLE_BADGE = {
  client: { label: "Client", className: "bg-[#1a1a1a] text-[#a3a3a3] border-[#262626]" },
  designer: { label: "Designer", className: "bg-[#FF2D2D]/10 text-[#FF2D2D] border-[#FF2D2D]/30" },
  admin: { label: "Studio", className: "bg-white/10 text-white border-white/20" },
};

export function ProjectMessages({ projectId }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/messages`);
      setMessages(data);
    } catch (_) {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000); // poll every 15s
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = body.trim();
    if (!text) return;
    setSending(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/messages`, { body: text });
      setMessages((m) => [...m, data]);
      setBody("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border border-[#262626] bg-[#0a0a0a]" data-testid="messages-panel">
      <div ref={scrollRef} className="max-h-[420px] min-h-[120px] overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="text-center text-[#a3a3a3] py-8">
            <ChatCircleText size={32} className="mx-auto mb-2" />
            No messages yet. Start the conversation.
          </div>
        ) : (
          messages.map((m) => {
            const isMine = m.sender_id === user?.id;
            const badge = ROLE_BADGE[m.sender_role] || ROLE_BADGE.client;
            return (
              <div key={m.id} className={`flex ${isMine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                <div className={`max-w-[80%] ${isMine ? "items-end" : "items-start"} flex flex-col gap-1`}>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-semibold">{m.sender_name}</span>
                    <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 border ${badge.className}`}>
                      {badge.label}
                    </span>
                    <span className="text-[#666]">{new Date(m.created_at).toLocaleString()}</span>
                  </div>
                  <div className={`px-4 py-2.5 text-sm whitespace-pre-wrap ${
                    isMine ? "bg-[#FF2D2D] text-white" : "bg-[#1a1a1a] text-white border border-[#262626]"
                  }`}>
                    {m.body}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
      <form onSubmit={send} className="border-t border-[#262626] p-3 flex gap-2">
        <input
          type="text"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Type a message…"
          data-testid="message-input"
          className="flex-1 bg-[#050505] border border-[#262626] px-4 py-2.5 text-sm focus:border-[#FF2D2D] outline-none"
        />
        <button
          type="submit"
          disabled={sending || !body.trim()}
          data-testid="message-send"
          className="btn-primary px-4 py-2.5 text-sm font-bold uppercase tracking-wider flex items-center gap-2 disabled:opacity-50"
        >
          <PaperPlaneTilt size={14} weight="fill" /> Send
        </button>
      </form>
    </div>
  );
}
